import React, { useState } from 'react';
import CategoryReportService from './CategoryReportService';

// Quote a CSV cell (labels contain commas) and prefix a UTF-8 BOM so Excel reads Thai.
const csvCell = (v) => {
    const s = v == null ? '' : String(v);
    return `"${s.replace(/"/g, '""')}"`;
};

const downloadCsv = (filename, rows) => {
    const body = rows.map((r) => r.map(csvCell).join(',')).join('\n');
    const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

const fmtJpy = (n) => `¥${Number(n || 0).toLocaleString()}`;

const CategoryReport = () => {
    const [trackingText, setTrackingText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [report, setReport] = useState(null);
    const [preparing, setPreparing] = useState(false);
    const [prepareResult, setPrepareResult] = useState('');
    const [prepareProgress, setPrepareProgress] = useState(null);

    // Tier-2 review (claude.ai)
    const [reviewPrompts, setReviewPrompts] = useState([]);
    const [chunkIdx, setChunkIdx] = useState(0);
    const [reviewCounts, setReviewCounts] = useState(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [importText, setImportText] = useState('');
    const [importResult, setImportResult] = useState('');
    const [importing, setImporting] = useState(false);
    const [copied, setCopied] = useState(false);

    const handleGenerate = async () => {
        setError('');
        setReport(null);
        if (!trackingText.trim()) {
            setError('กรุณาวางเลขพัสดุ (trackingJp) อย่างน้อย 1 รายการ');
            return;
        }
        setLoading(true);
        try {
            const res = await CategoryReportService.generateCategoryReport(trackingText);
            setReport(res.data);
        } catch (err) {
            setError(err?.response?.data?.message || 'เกิดข้อผิดพลาดในการสร้างรายงาน');
        } finally {
            setLoading(false);
        }
    };

    const handlePrepare = async () => {
        setError('');
        setPrepareResult('');
        setPrepareProgress(null);
        // Send trackings in small chunks so we can show live progress (a single
        // request over a big list can churn 30s+ with no feedback).
        const lines = trackingText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
        if (!lines.length) {
            setError('กรุณาวางเลขพัสดุ (trackingJp) อย่างน้อย 1 รายการ');
            return;
        }
        setPreparing(true);
        const CHUNK = 8;
        let enriched = 0;
        let categorized = 0;
        let matched = 0;
        let done = 0;
        setPrepareProgress({ done: 0, total: lines.length, enriched: 0, categorized: 0 });
        try {
            for (let i = 0; i < lines.length; i += CHUNK) {
                const chunk = lines.slice(i, i + CHUNK);
                // eslint-disable-next-line no-await-in-loop
                const res = await CategoryReportService.prepareBatch(chunk.join('\n'));
                enriched += res.data.enriched || 0;
                categorized += res.data.categorized || 0;
                matched += res.data.matched || 0;
                done += chunk.length;
                setPrepareProgress({ done, total: lines.length, enriched, categorized });
            }
            setPrepareResult(`✓ เสร็จแล้ว: ดึงชื่อ ${enriched} · จัดหมวด ${categorized} (จาก ${matched} ออร์เดอร์). กด "สร้างรายงาน" เพื่อดูผล`);
        } catch (err) {
            setError(err?.response?.data?.message || 'เตรียมข้อมูลไม่สำเร็จ');
        } finally {
            setPreparing(false);
        }
    };

    const exportSummaryCsv = () => {
        if (!report) return;
        const rows = [
            ['category', 'label', 'count', 'totalJpy'],
            ...report.summary.map((s) => [s.category, s.label, s.count, s.totalJpy]),
        ];
        downloadCsv('category-report-summary.csv', rows);
    };

    const handleBuildReview = async () => {
        setReviewPrompts([]);
        setChunkIdx(0);
        setReviewCounts(null);
        setReviewLoading(true);
        try {
            const res = await CategoryReportService.getReviewPacket(trackingText);
            setReviewPrompts(res.data.prompts || []);
            setReviewCounts(res.data.counts || null);
        } catch (err) {
            setError(err?.response?.data?.message || 'สร้าง prompt ไม่สำเร็จ');
        } finally {
            setReviewLoading(false);
        }
    };

    const copyPrompt = () => {
        const p = reviewPrompts[chunkIdx];
        if (p) {
            navigator.clipboard.writeText(p);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }
    };

    // Parse pasted claude.ai output — lines like "tracking,category" (comma or spaces).
    const parseAssignments = (text) =>
        text
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean)
            .map((line) => {
                const parts = line.split(/[\s,]+/);
                const category = parts[parts.length - 1];
                const tracking = parts.slice(0, -1).join('') || parts[0];
                return { tracking, category };
            })
            .filter((a) => a.tracking && /^\d+$/.test(a.category));

    const handleImport = async () => {
        setImportResult('');
        const assignments = parseAssignments(importText);
        if (!assignments.length) {
            setImportResult('ไม่พบบรรทัดที่อ่านได้ (รูปแบบ: เลขพัสดุ,เลขหมวด)');
            return;
        }
        setImporting(true);
        try {
            const res = await CategoryReportService.importReview(assignments);
            setImportText(''); // saved to DB — clear the box
            setImportResult(`✓ บันทึกแล้ว: ${res.data.appliedTrackings} พัสดุ (${res.data.updatedOrders} ออร์เดอร์). กด "สร้างรายงาน" อีกครั้งเพื่อดูผลล่าสุด`);
        } catch (err) {
            setImportResult(err?.response?.data?.message || 'นำเข้าไม่สำเร็จ');
        } finally {
            setImporting(false);
        }
    };

    const exportDetailCsv = () => {
        if (!report) return;
        const rows = [
            ['trackingJp', 'orderId', 'website', 'title', 'mercariCategory', 'category', 'categorizedBy', 'qty', 'unitJpy', 'lineJpy'],
            ...report.rows.map((r) => [
                r.trackingJp, r.orderId, r.website, r.title, r.categoryName,
                r.category, r.categorizedBy, r.qty, r.unitJpy, r.lineJpy,
            ]),
        ];
        downloadCsv('category-report-detail.csv', rows);
    };

    const exportNotMatchedCsv = () => {
        if (!report) return;
        const rows = [['type', 'value']];
        (report.unmatchedTrackings || []).forEach((t) => rows.push(['unmatched_tracking', t]));
        (report.noTracking || []).forEach((t) => rows.push(['no_tracking', t]));
        downloadCsv('category-report-not-matched.csv', rows);
    };

    return (
        <div className="p-4 max-w-5xl mx-auto">
            <h1 className="text-2xl font-bold mb-2">รายงานหมวดหมู่สินค้า (Category Report)</h1>
            <p className="text-gray-600 mb-4 text-sm">
                วางเลขพัสดุญี่ปุ่น (trackingJp) บรรทัดละ 1 เลข ระบบจะจับคู่ออร์เดอร์ จัดหมวดหมู่ตาม 59 หมวด และสรุปจำนวน + ยอดเงิน (เยน) ต่อหมวด
            </p>

            <textarea
                className="w-full h-40 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-sm"
                placeholder={'647763309572\nDA1234567890\n...'}
                value={trackingText}
                onChange={(e) => setTrackingText(e.target.value)}
            />

            <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button
                    onClick={handlePrepare}
                    disabled={preparing}
                    title="ดึงชื่อสินค้า (Yahoo/PayPay/เว็บที่รองรับ) แล้วจัดหมวดอัตโนมัติ เพื่อลดรายการที่ต้องรีวิวด้วย Claude"
                    className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50"
                >
                    {preparing
                        ? `กำลังเตรียม ${prepareProgress ? `${prepareProgress.done}/${prepareProgress.total}` : ''}...`
                        : 'ดึงชื่อ + จัดหมวดรอบนี้'}
                </button>
                <button
                    onClick={handleGenerate}
                    disabled={loading}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                    {loading ? 'กำลังสร้างรายงาน...' : 'สร้างรายงาน'}
                </button>
                {report && (
                    <>
                        <button onClick={exportSummaryCsv} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                            ดาวน์โหลด CSV (สรุป)
                        </button>
                        <button onClick={exportDetailCsv} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                            ดาวน์โหลด CSV (รายชิ้น)
                        </button>
                    </>
                )}
            </div>

            {preparing && prepareProgress && (
                <div className="mt-3">
                    <div className="text-sm text-emerald-800 mb-1">
                        กำลังดึงชื่อ + จัดหมวด: {prepareProgress.done}/{prepareProgress.total} พัสดุ · ดึงชื่อ {prepareProgress.enriched} · จัดหมวด {prepareProgress.categorized}
                    </div>
                    <div className="w-full h-2 bg-emerald-100 rounded">
                        <div
                            className="h-2 bg-emerald-500 rounded transition-all"
                            style={{ width: `${Math.round((prepareProgress.done / Math.max(prepareProgress.total, 1)) * 100)}%` }}
                        />
                    </div>
                </div>
            )}

            {!preparing && prepareResult && <div className="mt-3 p-3 bg-emerald-50 text-emerald-800 rounded-md text-sm">{prepareResult}</div>}

            {error && <div className="mt-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">{error}</div>}

            {report && (
                <div className="mt-6">
                    {/* Totals */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <div className="p-3 bg-gray-50 rounded-md">
                            <div className="text-xs text-gray-500">ออร์เดอร์ที่จับคู่ได้</div>
                            <div className="text-xl font-semibold">{report.meta.matchedOrders} / {report.meta.trackingCount}</div>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-md">
                            <div className="text-xs text-gray-500">จำนวนชิ้นรวม</div>
                            <div className="text-xl font-semibold">{report.totals.items}</div>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-md">
                            <div className="text-xs text-gray-500">ยอดรวม (เยน)</div>
                            <div className="text-xl font-semibold">{fmtJpy(report.totals.totalJpy)}</div>
                        </div>
                    </div>

                    {/* Notes */}
                    {!report.meta.haikuAvailable && report.meta.uncategorizedNoTitle >= 0 && (
                        <div className="mb-3 p-3 bg-yellow-50 text-yellow-800 rounded-md text-sm">
                            การจัดหมวดด้วย AI ปิดอยู่ (ยังไม่ได้ตั้งค่า ANTHROPIC_API_KEY) — สินค้าที่มีชื่อแต่ไม่ใช่ Mercari จะถูกจัดเข้า “อื่นๆ (59)”
                        </div>
                    )}
                    {report.meta.uncategorizedNoTitle > 0 && (
                        <div className="mb-3 p-3 bg-blue-50 text-blue-800 rounded-md text-sm">
                            {report.meta.uncategorizedNoTitle} ชิ้นไม่มีชื่อสินค้าที่บันทึกไว้ (เว็บอื่น/ออร์เดอร์เก่า) จึงถูกจัดเข้า “อื่นๆ (59)”
                        </div>
                    )}

                    {/* Summary table */}
                    <table className="w-full border border-gray-200 text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-3 py-2 text-left w-16">#</th>
                                <th className="px-3 py-2 text-left">หมวดหมู่</th>
                                <th className="px-3 py-2 text-right w-24">จำนวน</th>
                                <th className="px-3 py-2 text-right w-40">ยอดเงิน (เยน)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {report.summary.map((s) => (
                                <tr key={s.category} className="border-t border-gray-100">
                                    <td className="px-3 py-2">{s.category}</td>
                                    <td className="px-3 py-2">{s.label}</td>
                                    <td className="px-3 py-2 text-right">{s.count}</td>
                                    <td className="px-3 py-2 text-right">{fmtJpy(s.totalJpy)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {/* Not matched: unmatched trackings + no-tracking parcels */}
                    {(((report.unmatchedTrackings || []).length + (report.noTracking || []).length) > 0) && (
                        <div className="mt-4 p-3 border border-amber-200 bg-amber-50 rounded-md">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                <div className="text-sm font-medium text-amber-900">
                                    พัสดุที่ไม่เข้ารายงาน (input แต่จับคู่ไม่ได้) — {(report.unmatchedTrackings || []).length + (report.noTracking || []).length} พัสดุ
                                </div>
                                <button onClick={exportNotMatchedCsv} className="px-3 py-1 text-sm border border-amber-300 rounded-md hover:bg-amber-100">
                                    ดาวน์โหลด CSV
                                </button>
                            </div>
                            {(report.unmatchedTrackings || []).length > 0 && (
                                <div className="mb-2">
                                    <div className="text-xs text-amber-800 mb-1">มีเลขพัสดุ แต่หาออร์เดอร์ไม่เจอ ({report.unmatchedTrackings.length})</div>
                                    <div className="p-2 bg-white rounded text-xs font-mono break-all">{report.unmatchedTrackings.join(', ')}</div>
                                </div>
                            )}
                            {(report.noTracking || []).length > 0 && (
                                <div>
                                    <div className="text-xs text-amber-800 mb-1">ไม่มีเลขพัสดุ / No Tracking ({report.noTracking.length})</div>
                                    <div className="p-2 bg-white rounded text-xs break-all">{report.noTracking.join(' · ')}</div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Tier-2 review with claude.ai */}
                    {(report.meta.needsReview > 0 || report.meta.uncategorizedNoTitle > 0) && (
                        <div className="mt-6 p-4 border border-purple-200 rounded-md bg-purple-50">
                            <div className="font-semibold text-purple-900 mb-1">รีวิวเพิ่มเติมด้วย Claude (subscription)</div>
                            <p className="text-sm text-purple-800 mb-3">
                                รายการที่ AI ไม่มั่นใจ ({report.meta.needsReview}) และไม่มีชื่อสินค้า ({report.meta.uncategorizedNoTitle}) — สร้าง prompt ไปวางใน Claude ของคุณ (ไม่เสียค่า API) แล้วนำผลกลับมา
                            </p>

                            <button
                                onClick={handleBuildReview}
                                disabled={reviewLoading}
                                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                            >
                                {reviewLoading ? 'กำลังสร้าง...' : 'สร้าง Prompt รีวิว'}
                            </button>

                            {reviewPrompts.length > 0 && (
                                <div className="mt-3">
                                    <div className="flex items-center gap-3 mb-1 flex-wrap">
                                        <span className="text-sm text-purple-800">
                                            ชุด A (มีข้อมูล): {reviewCounts?.setA ?? 0} · ชุด B (เปิดลิงก์): {reviewCounts?.setB ?? 0}
                                        </span>
                                        {reviewPrompts.length > 1 && (
                                            <select
                                                value={chunkIdx}
                                                onChange={(e) => setChunkIdx(Number(e.target.value))}
                                                className="text-sm border border-purple-300 rounded-md px-2 py-1"
                                            >
                                                {reviewPrompts.map((_, i) => (
                                                    <option key={i} value={i}>{`Prompt ${i + 1} / ${reviewPrompts.length}`}</option>
                                                ))}
                                            </select>
                                        )}
                                        <button
                                            onClick={copyPrompt}
                                            className={`px-3 py-1 text-sm border rounded-md ${copied ? 'border-green-400 bg-green-50 text-green-700' : 'border-purple-300 hover:bg-purple-100'}`}
                                        >
                                            {copied ? 'คัดลอกแล้ว! ✓' : `คัดลอก Prompt ${reviewPrompts.length > 1 ? `(${chunkIdx + 1}/${reviewPrompts.length})` : ''}`}
                                        </button>
                                    </div>
                                    <textarea
                                        readOnly
                                        value={reviewPrompts[chunkIdx] || ''}
                                        className="w-full h-40 px-3 py-2 border border-purple-200 rounded-md font-mono text-xs bg-white"
                                    />
                                    {reviewPrompts.length > 1 && (
                                        <p className="text-xs text-purple-700 mt-1">มี {reviewPrompts.length} ชุด — วางทีละชุดใน Claude แล้วรวมผลมาวางด้านล่าง</p>
                                    )}
                                </div>
                            )}

                            <div className="mt-4">
                                <div className="text-sm font-medium text-purple-900 mb-1">วางผลลัพธ์จาก Claude ที่นี่ (บรรทัดละ: เลขพัสดุ,เลขหมวด)</div>
                                <textarea
                                    value={importText}
                                    onChange={(e) => setImportText(e.target.value)}
                                    placeholder={'647763309572,33\nDA1234567890,45'}
                                    className="w-full h-28 px-3 py-2 border border-purple-200 rounded-md font-mono text-xs bg-white"
                                />
                                <div className="mt-2 flex items-center gap-3">
                                    <button
                                        onClick={handleImport}
                                        disabled={importing}
                                        className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                                    >
                                        {importing ? 'กำลังนำเข้า...' : 'นำเข้าผล'}
                                    </button>
                                    {importResult && <span className="text-sm text-purple-800">{importResult}</span>}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CategoryReport;
